import { useTheme } from '@mui/material';
import { useMemo } from 'react';
import useButtonTokens from './component-tokens/useButtonTokens';

const useDesignTokens = () => {
  const theme = useTheme();

  const buttonTokens = useButtonTokens();

  const tokens = useMemo(() => {
    return {
      components: {
        scrollbar: {
          bg: theme.palette.grey[200],
          bg_hover: theme.palette.grey[300],
        },
        button: buttonTokens,
        avatar: {
          border: {
            contrast: 'rgba(0, 0, 0, 0.08)',
          },
        },
      },
      colors: {
        brand: {
          primary: theme.palette.primary[900],
        },
        text: {
          primary: theme.palette.grey[800],
          secondary: theme.palette.grey[500],
          white: theme.palette.common.white,
          disabled: {
            primary: theme.palette.grey[200],
            high: theme.palette.grey[300],
          },
          placeholder: theme.palette.grey[400],
          success: {
            primary: theme.palette.success[900],
            secondary: theme.palette.success[700],
            tertiary: theme.palette.success.main,
          },
          error: {
            primary: theme.palette.error[900],
            secondary: theme.palette.error[700],
            tertiary: theme.palette.error[400],
          },
          warning: {
            primary: theme.palette.warning[900],
            secondary: theme.palette.warning[700],
            tertiary: theme.palette.warning.main,
          },
          brand: {
            primary: theme.palette.primary[900],
            secondary: theme.palette.primary[600],
            tertiary: theme.palette.primary[300],
          },
          highlight: {
            primary: theme.palette.secondary[700],
            secondary: theme.palette.secondary[500],
            tertiary: theme.palette.secondary[300],
          },
        },
        border: {
          primary: theme.palette.grey[100],
          secondary: theme.palette.grey[200],
          tertiary: theme.palette.grey[300],
          disabled: theme.palette.grey[200],
          brand: theme.palette.primary[200],
          brandSolid: theme.palette.primary[500],
          highlight: theme.palette.secondary[100],
          highlightLight: theme.palette.secondary['700-24'],
          highlightSolid: theme.palette.secondary[700],
          error: theme.palette.error[200],
          errorSolid: theme.palette.error[400],
          success: theme.palette.success[200],
          successSolid: theme.palette.success[400],
          warning: theme.palette.warning[200],
          warningSolid: theme.palette.warning[400],
          grayFocusRing: theme.palette.grey[50],
          brandFocusRing: theme.palette.primary['600-12'],
          errorFocusRing: theme.palette.error['600-12'],
        },
        bg: {
          primary: theme.palette.common.white,
          primaryHover: theme.palette.grey[25],
          primaryInverse: theme.palette.grey[700],
          secondary: theme.palette.grey[25],
          secondaryHover: theme.palette.grey[50],
          tertiary: theme.palette.grey[100],
          tertiaryHover: theme.palette.grey[200],
          active: theme.palette.secondary['700-8'], // cyan-700 with 8% opacity
          hover: theme.palette.grey[100], // bg-tertiary
          disabled: theme.palette.grey[25],
          placeholder: theme.palette.grey[50],
          overlay: theme.palette.grey.black70,
          brand: {
            primary: theme.palette.primary[50],
            secondary: theme.palette.primary[100],
            solidPrimary: theme.palette.primary[700],
            solidSecondary: theme.palette.primary[600],
          },
          highlight: {
            primary: theme.palette.secondary[50],
            secondary: theme.palette.secondary[100],
            solid: theme.palette.secondary[700],
          },
          error: {
            primary: theme.palette.error[50],
            secondary: theme.palette.error[100],
            solid: theme.palette.error[400],
          },
          warning: {
            primary: theme.palette.warning[50],
            secondary: theme.palette.warning[100],
            solid: theme.palette.warning[400],
          },
          success: {
            primary: theme.palette.success[50],
            secondary: theme.palette.success[100],
            solid: theme.palette.success[400],
          },
          cta: {
            primary: theme.palette.secondary['700-8'], // cyan-700 with 8% opacity
          },
        },
        fg: {
          primary: theme.palette.grey[700],
          secondary: theme.palette.grey[400],
          secondaryHover: theme.palette.grey[700],
          white: theme.palette.common.white,
          disabled: theme.palette.grey[200],
          placeholder: theme.palette.grey[200],
          brand: {
            primary: theme.palette.primary[500],
            secondary: theme.palette.primary[300],
          },
          highlight: {
            primary: theme.palette.secondary[700],
            secondary: theme.palette.secondary[100],
          },
          error: {
            primary: theme.palette.error[400],
            secondary: theme.palette.error[300],
          },
          warning: {
            primary: theme.palette.warning[400],
            secondary: theme.palette.warning[300],
          },
          success: {
            primary: theme.palette.success[400],
            secondary: theme.palette.success[300],
          },
        },
        boxShadow: {
          primary: theme.palette.primary.primaryShadowColor,
          error: theme.palette.error.errorShadowColor,
        },
      },
      radius: {
        xs: theme.sizeGuide.xs,
        full: '9999px',
      },
    };
  }, [theme, buttonTokens]);

  return tokens;
};

export default useDesignTokens;
